import type { FallbackTier } from "@sikhio/shared";
import { PanelHeader } from "./PanelHeader";

export interface HighwayEvent {
  id: string;
  type: string;
  description: string;
  severity: "low" | "medium" | "high";
}

export interface HighwayStatus {
  routeId: string;
  routeName: string;
  routeNameEn: string;
  eventCount: number;
  events: HighwayEvent[];
  approachStatus: "clear" | "slow" | "congested";
  travelTimeMin: number;
  normalTravelTimeMin: number;
  delayMin: number;
  note: string | null;
  source: string;
  observedAt: string;
}

interface Props {
  data: HighwayStatus | null;
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
}

const STATUS_COLOR: Record<HighwayStatus["approachStatus"], string> = {
  clear: "var(--good)",
  slow: "var(--warn)",
  congested: "var(--bad)",
};

const STATUS_LABEL: Record<HighwayStatus["approachStatus"], string> = {
  clear: "ปกติ",
  slow: "เริ่มหนาแน่น",
  congested: "คับคั่ง",
};

const STATUS_LABEL_EN: Record<HighwayStatus["approachStatus"], string> = {
  clear: "CLEAR",
  slow: "SLOW",
  congested: "CONGESTED",
};

export function HighwayPanel({ data, loading, ageMinutes, fallbackTier }: Props) {
  if (loading && !data) {
    return (
      <div className="col">
        <div className="eyebrow">ทางหลวง / HIGHWAY</div>
        <div className="skeleton" style={{ height: 12, marginTop: 8 }} />
      </div>
    );
  }
  if (!data) return null;

  const statusColor = STATUS_COLOR[data.approachStatus];
  const statusLabel = STATUS_LABEL[data.approachStatus];
  const statusLabelEn = STATUS_LABEL_EN[data.approachStatus];

  return (
    <div className="col" style={{ gap: 4 }}>
      <PanelHeader
        title="มิตรภาพ "
        fallbackTier={fallbackTier}
        ageMinutes={ageMinutes}
        source="itic·longdo"
      />

      {/* Status badge */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
        <span style={{
          background: statusColor,
          color: data.approachStatus === "slow" ? "var(--fixed-dark)" : "var(--fixed-light)",
          fontSize: "var(--size-eyebrow)",
          fontWeight: 700,
          padding: "2px 8px",
          letterSpacing: "0.06em",
        }}>
          {statusLabelEn}
        </span>
        <span className="caption" style={{ color: "var(--text-2)" }}>{statusLabel}</span>
        {data.eventCount > 0 && (
          <span className="mono caption" style={{ marginLeft: "auto", color: "var(--warn)" }}>
            {data.eventCount} เหตุการณ์
          </span>
        )}
      </div>

      {/* Travel time */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "4px 12px", alignItems: "baseline" }}>
        <span className="caption" style={{ color: "var(--text-2)" }}>โคราช to สีคิ้ว</span>
        <span className="mono caption" style={{ textAlign: "right" }}>
          {data.travelTimeMin} นาที
          {data.delayMin > 0 && (
            <span style={{ color: "var(--bad)", marginLeft: 4 }}>+{data.delayMin}'</span>
          )}
        </span>

        <span className="caption" style={{ color: "var(--text-2)" }}>ปกติ</span>
        <span className="mono caption" style={{ textAlign: "right", color: "var(--text-3)" }}>
          {data.normalTravelTimeMin} นาที
        </span>

        <span className="caption" style={{ color: "var(--text-2)" }}>เส้นทาง</span>
        <span className="mono caption" style={{ textAlign: "right", color: "var(--text-3)" }}>
          ทล. 2
        </span>
      </div>

      {/* Active events */}
      {data.events.length > 0 && (
        <div style={{ marginTop: 4, borderTop: "1px solid var(--line)", paddingTop: 4 }}>
          {data.events.slice(0, 3).map((ev) => (
            <div key={ev.id} className="caption" style={{ color: "var(--text-2)", marginBottom: 2 }}>
              <span style={{ color: "var(--warn)", marginRight: 4 }}>▲</span>
              {ev.description}
            </div>
          ))}
        </div>
      )}

      {data.note && (
        <div className="caption" style={{ color: "var(--accent)", marginTop: 2, fontStyle: "italic" }}>
          {data.note}
        </div>
      )}
    </div>
  );
}
